/**
 * Wallet store — the user's coin balance.
 *
 * Fetching the balance is also what triggers the free tier's daily top-up on the
 * backend, so `refresh()` is called on entering the app, not only where the
 * balance is rendered. This store is NOT persisted: a stale balance shown after
 * a reload would be worse than a brief empty one.
 */

import { create } from 'zustand'
import { getWallet } from '@/services/api'

interface WalletStoreState {
  balance: number | null
  planCode: string | null
  periodEnd: string | null
  isLoading: boolean
  refresh: () => Promise<void>
  clear: () => void
}

// Bumped on every clear() so a read started before a logout cannot write the
// previous account's balance back into the store when it lands.
let generation = 0

export const useWalletStore = create<WalletStoreState>((set) => ({
  balance: null,
  planCode: null,
  periodEnd: null,
  isLoading: false,

  refresh: async () => {
    const startedAt = generation
    set({ isLoading: true })
    try {
      const wallet = await getWallet()
      if (startedAt !== generation) return
      set({
        balance: wallet.balance,
        planCode: wallet.plan_code,
        periodEnd: wallet.period_end,
        isLoading: false,
      })
    } catch {
      // A failed read must not blank an already displayed balance.
      if (startedAt !== generation) return
      set({ isLoading: false })
    }
  },

  clear: () => {
    generation += 1
    set({ balance: null, planCode: null, periodEnd: null, isLoading: false })
  },
}))
